import {h, Component} from 'preact'
import {HomepageElement} from '../homepage'
import {isHtmlElement} from '../../libs/preact-entry'
import HomepageEditor from './editor'
import OrderAnimator from './orderAnimator'
import Portal from 'preact-portal'
import NewWidgetButton, { TypesThatCanBeAdded, widgetsToAddFullWidth, widgetsToAddMainContent, widgetsToAddSidebar } from './new-button'
import {arrayMove} from 'react-sortable-hoc'
import {MovePayload, DonePayload, VisiblePayload} from './toolbar'

interface ColumnProps {
  classPrefix: string,
  container: string,
  onChildMouseOver?: () => void,
  onChildMouseOut?: () => void,
  elements: Array<HomepageElement>,
  widgetList?: Array<widgetsToAddSidebar | widgetsToAddFullWidth | widgetsToAddMainContent>,
  onChange: (els: Array<HomepageElement>) => void,
  nested: boolean
}

interface ColumnState {
  editingId: number | string | null
}

const getDefaultValue = (type: TypesThatCanBeAdded, id: number): HomepageElement => {
  const sharedDefault = {
    active: false,
    id
  }
  switch (type) {
    case 'html_widget':
      return Object.assign({}, sharedDefault, {
        type,
        title: '',
        config: {
          html: ''
        }
      })
    case 'badges':
      return Object.assign({}, sharedDefault, {
        type,
        title: 'Badges',
        config: {
          amount: 5
        }
      })
    case 'tag_cloud':
      return Object.assign({}, sharedDefault, {
        type,
        title: 'Popular tags',
        config: {
          amount: 5
        }
      })
    case 'solved_topics':
      return Object.assign({}, sharedDefault, {
        type,
        title: 'Solved topics',
        config: {
          amount: 5
        }
      })
    case 'most_liked':
      return Object.assign({}, sharedDefault, {
        type,
        title: 'Most Liked',
        config: {
          amount: 5,
          period: 7
        }
      })
    case 'community_statistics':
      return Object.assign({}, sharedDefault, {
        type,
        title: 'Community Statistics',
        config: {
          total_topics: true,
          total_comments: true,
          total_users: true
        }
      })
  }
}

export default class WidgetColumn extends Component<ColumnProps, ColumnState> {
  constructor() {
    super()
    this.state = {
      editingId: null
    }
    this.onDone = this.onDone.bind(this)
    this.onRemove = this.onRemove.bind(this)
  }

  onMove(positionToMoveUp: number, {direction}: MovePayload) {
    if (direction === 'down') {
      positionToMoveUp += 1
    }
    this.props.onChange(arrayMove(this.props.elements, positionToMoveUp, positionToMoveUp - 1))
  }

  onDone(change: DonePayload) {
    this.setState({
      editingId: null
    })
    this.props.onChange(
      this.props.elements.map((el, index) => {
        if (index !== change.idx) return el
        if (change.element.type !== el.type) return el
        return Object.assign({}, el, change.element)
      })
    )
  }

  onVisibleToggle(idx: number, visible: boolean) {
    const elements = this.props.elements.map((el, index) => {
      if (idx !== index) return el
      return {
        ...el,
        active: visible
      }
    })
    this.props.onChange(elements)
  }

  onRemove(removedIndex: number) {
    const elements = this.props.elements.filter((_, index) => removedIndex !== index)
    this.props.onChange(elements)
  }

  onAddWidget(type: TypesThatCanBeAdded) {
    const newWidgetButton = document.querySelector(`${this.props.container} .widget-editor-new-button`)

    const newId = this.props.elements.length
    const newElement = getDefaultValue(type, newId)
    const lastElement = this.props.elements.pop()

    let elements

    if (!lastElement) {
      elements = this.props.elements.concat([newElement])
    } else if (lastElement.type === 'footer') {
      elements = this.props.elements.concat([newElement, lastElement])
    } else {
      elements = this.props.elements.concat([lastElement, newElement])
    }

    if (!newWidgetButton) return
    const container = document.createElement('div')
    container.classList.add(
      'homepage-widget-container',
      `homepage-widget-container--${type}`,
      `${this.props.classPrefix}${newId}`,
      'homepage-widget-container--inactive'
    )

    const placeholder = document.createElement('div')
    placeholder.classList.add('homepage-widget-placeholder')
    container.appendChild(placeholder)

    newWidgetButton.parentElement?.insertBefore(container, newWidgetButton)

    this.setState({ editingId: newId })
    this.props.onChange(elements)
  }

  canMoveUp(isMovable: boolean, idx: number) {
    if (this.props.nested) {
      return idx > 0
    }
    return isMovable && idx > 2
  }

  canMoveDown(isMovable: boolean, idx: number, totalCount: number) {
    if (this.props.nested) {
      return idx < (totalCount - 1)
    }
    return isMovable && idx < totalCount - 2
  }

  render () {
    return <div>
      {
        this.props.elements.map((el, idx, {length}) => {
          const className = `.${this.props.classPrefix}${el.id}`
          const widgetContainer = document.querySelector(className)
          if (!isHtmlElement(widgetContainer)) {
            throw Error(`Expected "${className}" to be in the DOM but it was not found`)
          }

          const isMovable = ['footer', 'header', 'navigation_bar'].indexOf(el.type) < 0

          return <OrderAnimator isMovable={isMovable} idx={idx} id={el.id} key={el.id} into={widgetContainer}>
            <HomepageEditor
              idx={idx}
              edit={el.id === this.state.editingId}
              isMovable={isMovable}
              homepageElement={el}
              totalCount={length}
              parentEl={widgetContainer}
              canMoveUp={this.canMoveUp(isMovable, idx)}
              canMoveDown= {this.canMoveDown(isMovable, idx, length)}
              onEdit={() => this.setState({editingId: el.id})}
              onDone={this.onDone}
              onClose={() => this.setState({editingId: null})}
              onMove={dir => this.onMove(idx, dir)}
              onVisibleToggle={(visible: VisiblePayload) => this.onVisibleToggle(idx, visible)}
              onRemove={() => this.onRemove(idx)}
              onMouseOut={this.props.onChildMouseOut}
              onMouseOver={this.props.onChildMouseOver}
            />
          </OrderAnimator>
        })
      }
      {
        this.props.widgetList && <Portal key='portal' into={`${this.props.container} .widget-editor-new-button`}>
          <NewWidgetButton widgetList={this.props.widgetList} onClick={(type: TypesThatCanBeAdded) => this.onAddWidget(type)} />
        </Portal>
      }
    </div>
  }
}
